import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { toast } from "react-toastify";
import { Dropdown } from "primereact/dropdown";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { ContentBody, ContentHeader } from "./styled";
import { PasswordGenerator } from "../../components/password-generator";
import Error from "../../../../components/Error";
import { ModalSignature } from "../../subscriptions/modal-signature";

//services
import { createUser } from "../../../../services/manager/user.service";
import { createFavoriteBulk } from "../../../../services/create-account/create-account.service";
import { bulkStorageFavoriteMaterials } from "../../../../utils/storageFavoriteMaterials";

const validationSchema = Yup.object({
	user: Yup.string()
		.max(40, "Nome muito longo")
		.required("Este campo é obrigatório")
		.matches(/^[aA-zZÀ-ÿ\s]+$/, "Este campo não pode conter números"),
	responsible: Yup.string()
		.max(40, "Nome do responsável muito longo")
		.required("Este campo é obrigatório")
		.matches(/^[aA-zZÀ-ÿ\s]+$/, "Este campo não pode conter números"),
	email: Yup.string()
		.email("E-mail inválido")
		.required("Este campo é obrigatório"),
	password: Yup.string().required("Este campo é obrigatório"),
	userType: Yup.string().required("Este campo é obrigatório"),
	sendMail: Yup.boolean(),
}).required();

export const NewUser = (): JSX.Element => {
	const { t: translation } = useTranslation();
	const {
		register,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm({ resolver: yupResolver(validationSchema) });

	const [loading, setLoading] = useState<boolean>(false);
	const [cleanPassword, setCleanPassword] = useState<boolean>(false);
	const [cleanFields, setCleanFields] = useState<boolean>(false);
	const [loadingModal, setLoadingModal] = useState<boolean>(false);
	const [showNewSignature, setShowNewSignature] = useState<boolean>(false);
	const [userTypeSelected, setUserTypeSelected] = useState<string>("");
	const [strengthPassword, setStrengthPassword] = useState<number>(0);
	const [userObj, setUserObj] = useState<any>();

	const userTypeOptions = [
		{ label: "Assinante", value: "Assinante" },
		{ label: "Parceiro", value: "Parceiro" },
		{ label: "Visitante", value: "Visitante" },
	];

	const onSubmit = (formData: any, e: any) => {
		e.preventDefault();
		setCleanPassword(false);
		setLoading(true);
		let obj = {
			name: formData.user,
			username: formData.responsible,
			email: formData.email,
			password: formData.password,
			type_user: formData.userType,
			send_email: formData.sendMail,
		};

		if (formData.userType === "Assinante") {
			setShowNewSignature(true);
			setLoading(false);
			setUserObj(obj);
		} else {
			const favorites = bulkStorageFavoriteMaterials();

			createUser(obj)
				.then(() => {
					// Create Favorite bulk call
					createFavoriteBulk(favorites).then(() => {
						setLoading(false);
						toast.success("Usuário criado com sucesso!");
						reset();
						reset({ sendMail: true });
						setUserTypeSelected("");
						setCleanPassword(true);
					});
				})
				.catch((err) => {
					setLoading(false);
					toast.error(err.response.data.error);
				});
		}
	};

	useEffect(() => {
		setValue("sendMail", true);
	}, []);

	useEffect(() => {
		if (cleanFields) {
			reset();
			setUserTypeSelected("");
			setCleanPassword(true);
		} else {
			setCleanPassword(false);
		}
	}, [cleanFields]);

	return (
		<Container fluid>
			<ContentHeader>
				<Row>
					<div className="title">Adicionar novo usuário</div>
				</Row>
			</ContentHeader>
			<ContentBody>
				<Row className="mt-2">
					<Col
						xs={12}
						md={9}
						lg={4}
					>
						<Form onSubmit={handleSubmit(onSubmit)}>
							<Form.Group className="mb-2">
								<Form.Label className="text-darker-grey">
									{translation("manager.user.displayName")}:
								</Form.Label>
								<Form.Control
									maxLength={40}
									type="text"
									placeholder={translation(
										"manager.user.displayName"
									)}
									{...register("user", { required: true })}
								/>
								{errors.user ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.user?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</Form.Group>

							<Form.Group className="mb-2">
								<Form.Label className="text-darker-grey">
									{translation("manager.user.responsible")}:
								</Form.Label>
								<Form.Control
									maxLength={40}
									type="text"
									placeholder={translation(
										"manager.user.responsible"
									)}
									{...register("responsible", {
										required: true,
									})}
								/>
								{errors.responsible ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.responsible?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</Form.Group>

							<Form.Group className="mb-2">
								<Form.Label className="text-darker-grey">
									{translation("manager.user.email")}:
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={translation(
										"manager.user.email"
									)}
									{...register("email", { required: true })}
								/>
								{errors.email ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.email?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</Form.Group>

							<PasswordGenerator
								setValue={setValue}
								setStrengthPassword={setStrengthPassword}
								cleanPassword={cleanPassword}
							/>
							{errors.password ? (
								<div className="w-100">
									{" "}
									<Error>
										{errors.password?.message}
									</Error>{" "}
								</div>
							) : (
								""
							)}

							<Form.Group className="mb-2 mt-2">
								<Form.Label className="text-darker-grey">
									{translation("manager.user.userType")}:
								</Form.Label>
								<>
									<Dropdown
										className="select-form"
										value={userTypeSelected}
										options={userTypeOptions}
										onChange={(e) => {
											setValue("userType", e.value);
											setUserTypeSelected(e.value);
										}}
										placeholder="Selecione um tipo de usuário"
									/>
								</>
								{errors.userType ? (
									<div className="w-100">
										{" "}
										<Error>
											{errors.userType?.message}
										</Error>{" "}
									</div>
								) : (
									""
								)}
							</Form.Group>

							<Form.Check
								className="mb-3"
								type="checkbox"
								id="sendMail"
								{...register("sendMail")}
								label={translation("manager.user.checkEmail")}
							/>

							<Button
								variant="primary"
								type="submit"
								className="btn-rounded mb-1"
								disabled={
									(strengthPassword !== 0 &&
										strengthPassword < 75) ||
									loading
										? true
										: false
								}
							>
								{translation("manager.user.createUser")}
							</Button>

							{strengthPassword !== 0 && strengthPassword < 75 ? (
								<Error>
									A sua senha precisa ser boa ou forte para
									continuar
								</Error>
							) : (
								<></>
							)}
							{loading && (
								<Spinner
									animation="border"
									variant="primary"
									style={{
										width: "1.2rem",
										height: "1.2rem",
										borderWidth: "0.15em",
										borderRightColor: "transparent",
										marginLeft: "7px",
									}}
								/>
							)}
						</Form>
					</Col>
				</Row>
			</ContentBody>
			<ModalSignature
				showModal={showNewSignature}
				setShowModal={setShowNewSignature}
				isNewSignture={true}
				loading={loadingModal}
				setLoading={setLoadingModal}
				userObj={userObj}
				setCleanFields={setCleanFields}
			/>
		</Container>
	);
};
